import { debounce } from 'lodash';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box } from '@cad-arc';
import {
    ESRI_EXTENT_RECOVERY_RETRY_LIMIT,
    ESRI_HIGHLIGHT_FILL_OPACITY,
    ESRI_MAP_DEFAULT_ROTATION_ENABLED,
} from '../configurations';
import { Extent, Point, WebMap, webMercatorUtils, Color } from '../constructors';
import { storageHelpers } from '../../common/helpers/browser-storage';
import { isValidLatLng } from '../../location/helpers';
import type { GeoPoint, MarginBounds } from '../../location/types';
import type {
    EsriMapInterface,
    MapMode,
    WebGLUnavailableReason,
    MapOnChangeParams,
    MapViewpoint,
} from '../types';
import { MAIN_MAP_SURFACE, MAP_SURFACE_ATTRIBUTE } from '../types';
import { logMapError } from '../helpers/utils';
import { incrementLayerviewCreateErrorCount } from '../helpers/map-diagnostic-context';
import { getWebGLUnavailableReason } from '../helpers/webgl-unavailable-reason';
import { useEnabledFeatures } from '../../common/hooks/enabled-features/use-enabled-features';
import { useEsriIdentityContext } from '../esri-identity-context';
import {
    createInitialWebMap,
    loadPortalWebMap,
    PortalWebMapLoadResponse,
    readViewpoint,
    usableViewpoint,
} from './helpers';
import { useOwnedMapView } from './use-owned-map-view';
import { useEsriMapStore } from '~/map-window/hooks/use-esri-map-store';
import { MAP_ALTERNATIVE_BASEMAP_ACTIVATION_DATE_KEY } from '../configurations';

// eslint-disable-next-line no-restricted-imports
import '@arcgis/core/assets/esri/themes/dark/main.css';
import { colors } from '~/colors/colors';

/**
 * Esri Map used in map window
 */
type UseEsriESModuleMapProps = {
    zoom: number;
    mapRef: React.MutableRefObject<HTMLDivElement | null>;
    mapOnChange: (data: MapOnChangeParams) => void;
    center?: GeoPoint;
    isAlternativeBasemap?: boolean;
    webmapPortalId?: string;
    shouldResetMapOnInitialLoad?: boolean;
    mapMode?: MapMode;
    /** Build the view here instead of at `center` and `zoom`. */
    viewpointToRestore?: MapViewpoint;
    /** Told where the view was, just before it is torn down. */
    onViewTornDown?: (viewpoint: MapViewpoint) => void;
};
const useEsriESModuleMap = ({
    zoom,
    mapRef,
    mapOnChange,
    center,
    isAlternativeBasemap,
    webmapPortalId,
    shouldResetMapOnInitialLoad,
    mapMode,
    viewpointToRestore,
    onViewTornDown,
}: UseEsriESModuleMapProps): EsriMapInterface => {
    const enabledFeatures = useEnabledFeatures();
    // Read when the view is built and torn down, never as dependencies: neither should
    // rebuild the view.
    const viewpointToRestoreRef = useRef(viewpointToRestore);
    viewpointToRestoreRef.current = viewpointToRestore;
    const onViewTornDownRef = useRef(onViewTornDown);
    onViewTornDownRef.current = onViewTornDown;
    // Set when a view is built at a restored viewpoint. The effect that follows `center`
    // runs once as soon as the view exists, and would pull it straight back.
    const skipNextCenterSyncRef = useRef(false);
    // Read when the view is built, never as a dependency: changing modes swaps the basemap
    // on the live view (the map mode toggle does that) instead of rebuilding the view.
    const initialBasemapRef = useRef({ mapMode, useBasemapForMode: false });
    initialBasemapRef.current = {
        mapMode,
        useBasemapForMode: enabledFeatures.mapTileCacheEnabled === true,
    };

    const { refreshCredential, getEsriToken } = useEsriIdentityContext();
    // Ref'd because its identity can churn for reasons unrelated to the map (e.g. the API
    // client changing), and reacting to that would tear down and recreate the
    // MapView/WebGL context for nothing.
    const refreshCredentialRef = useRef(refreshCredential);
    refreshCredentialRef.current = refreshCredential;

    // Construction, teardown and health monitoring of the MapView all belong to
    // `useOwnedMapView`. This hook previously created the view itself and returned its
    // teardown from the same effect that sets `mapView` — so the cleanup ran right after
    // mount and never on the real unmount, and `view.destroy()` was never called at all.
    const { mapView, createMapView, addDisposer } = useOwnedMapView({
        surface: MAIN_MAP_SURFACE,
        mapRef,
        releaseWebGLContextOnTeardown:
            enabledFeatures.mapReleaseWebglContextOnTeardownEnabled === true,
        countSuspendCycles: true,
    });

    const esriMapStore = useEsriMapStore();
    // Gated on the Esri OAuth flag: with it off, the portal webmap loads exactly as before.
    const shouldLoadPortalWebMapFirst =
        !!webmapPortalId && enabledFeatures.esriOauthTokenEnabled === true;
    const [portalWebMapLoadResponse, setPortalWebMapLoadResponse] =
        useState<PortalWebMapLoadResponse>();
    const [webMap, setWebMap] = useState<WebMap>();
    const [webGLUnavailable, setWebGLUnavailable] = useState<WebGLUnavailableReason>();
    const [isMapLoading, setIsMapLoading] = useState<boolean>(true);
    const [isMapLoadingFailed, setIsMapLoadingFailed] = useState<boolean>(false);
    const prevCenterRef = useRef<GeoPoint>();
    const prevZoomRef = useRef<number>();
    const extentRef = useRef<Extent | undefined>();
    const readyRef = useRef(false);
    const retriesRef = useRef(0);
    const initialZoom = useRef<number>(zoom);
    const setMapLoadFailed = useCallback((failed: boolean) => {
        if (failed) {
            storageHelpers.setItem(
                MAP_ALTERNATIVE_BASEMAP_ACTIVATION_DATE_KEY,
                new Date().toISOString()
            );
        }
        setIsMapLoadingFailed(failed);
    }, []);

    useEffect(() => {
        // Guard on the coordinates, not just on `center` being present. A center object whose
        // latitude/longitude are missing is truthy, and `new Point` on it logs
        // "Missing value for required property 'x'" once per render. The effect below, which
        // actually builds the MapView, already checks validity this way — so an invalid center
        // produced a stream of Point errors next to a map that never initialized.
        if (!center || !isValidLatLng(center.latitude, center.longitude)) {
            return;
        }
        if (mapView && skipNextCenterSyncRef.current) {
            skipNextCenterSyncRef.current = false;
            return;
        }
        prevCenterRef.current = { latitude: center!.latitude, longitude: center!.longitude };

        const point = new Point(center);

        if (!mapView || point.equals(mapView.center)) {
            return;
        }

        mapView
            .goTo({
                center: [center.longitude, center.latitude],
            })
            .catch(() => {});
    }, [center, mapView]);

    useEffect(
        function loadPortalWebMapBeforeView() {
            if (!shouldLoadPortalWebMapFirst || !webmapPortalId || !!mapView) {
                return;
            }

            let isStale = false;

            loadPortalWebMap({ webmapPortalId, isAlternativeBasemap }).then((response) => {
                if (isStale) {
                    return;
                }
                if (response.status === 'unavailable') {
                    esriMapStore.setIsUsingFallbackWebMap(true);
                }
                setPortalWebMapLoadResponse(response);
            });

            return () => {
                isStale = true;
            };
        },
        [shouldLoadPortalWebMapFirst, webmapPortalId, mapView, isAlternativeBasemap, esriMapStore]
    );

    useEffect(() => {
        if (!!mapView || !center || !isValidLatLng(center?.latitude, center?.longitude)) {
            return;
        }

        const preloadedResponse =
            shouldLoadPortalWebMapFirst &&
            portalWebMapLoadResponse?.webmapPortalId === webmapPortalId
                ? portalWebMapLoadResponse
                : undefined;
        // Build the view only once the portal webmap load has finished.
        if (shouldLoadPortalWebMapFirst && !preloadedResponse) {
            return;
        }

        const { webmap, cancelPendingLoadEffects } = createInitialWebMap({
            webmapPortalId,
            portalWebMapLoadResponse: preloadedResponse,
            isAlternativeBasemap,
            setMapLoadFailed,
            // cad.map_tile_cache.enabled: start on the user's basemap rather than light-gray,
            // so a rebuild loads one basemap instead of two.
            ...(initialBasemapRef.current.useBasemapForMode
                ? { mapMode: initialBasemapRef.current.mapMode, useBasemapForMode: true }
                : {}),
            refreshCredential: () => refreshCredentialRef.current(),
            getEsriToken,
        });

        const restored = usableViewpoint(viewpointToRestoreRef.current);
        const startCenter = restored?.center ?? center;

        // Create View
        const view = createMapView({
            container: mapRef.current || undefined,
            map: webmap,
            center: startCenter as Point,
            zoom: restored?.zoom ?? initialZoom.current,
            constraints: {
                rotationEnabled: ESRI_MAP_DEFAULT_ROTATION_ENABLED,
            },
            highlightOptions: {
                color: new Color(colors.esriColors.highlightColor),
                fillOpacity: ESRI_HIGHLIGHT_FILL_OPACITY,
                haloOpacity: 1,
            },
        });
        addDisposer(view, cancelPendingLoadEffects);
        // Disposers run before the view is destroyed, so it can still say where it was.
        addDisposer(view, () => {
            const viewpoint = readViewpoint(view);
            if (viewpoint) {
                onViewTornDownRef.current?.(viewpoint);
            }
        });
        skipNextCenterSyncRef.current = restored !== undefined;

        view.when(() => {
            if (window.Cypress || process.env.NODE_ENV === 'development') {
                window.esriMapView = view;
                window.esriMapRef = mapRef;

                const canvases = mapRef.current?.querySelectorAll('canvas');
                if (canvases && canvases.length > 0) {
                    const canvas = canvases[0];
                    window.esriMapViewCanvas = canvas;
                }
            }
        }).catch((error) => {
            // Logged by `useOwnedMapView`; this hook only owns its own load state.
            const reason = getWebGLUnavailableReason(error);
            setWebGLUnavailable(reason);
            if (reason === 'stopped-working') {
                // A GPU problem, not a basemap one: don't move this machine to the
                // alternative basemap for the next 16 hours.
                setIsMapLoadingFailed(true);
            } else {
                setMapLoadFailed(true);
            }
        });

        // Set General popup rules
        view.popup.dockOptions = {
            position: 'bottom-center',
        };

        // Initialize prevCenter and prevZoom values
        prevCenterRef.current = startCenter;
        prevZoomRef.current = view.zoom;

        const resetExtent = async () => {
            if (!prevCenterRef.current) {
                return;
            }

            const center = { ...prevCenterRef.current };

            try {
                await view.goTo({
                    center: [center.longitude + 0.000001, center.latitude],
                    zoom: prevZoomRef.current,
                });

                await view.goTo({
                    center: [center.longitude, center.latitude],
                    zoom: prevZoomRef.current,
                });
            } catch (e) {
                // Swallow error.
            }
        };

        const getMarginBounds = (): MarginBounds | undefined => {
            if (!view.extent) {
                return;
            }

            const northWestLngLat = webMercatorUtils.xyToLngLat(view.extent.xmin, view.extent.ymax);
            const southEastLngLat = webMercatorUtils.xyToLngLat(view.extent.xmax, view.extent.ymin);

            return {
                nw: { latitude: northWestLngLat[1], longitude: northWestLngLat[0] },
                se: { latitude: southEastLngLat[1], longitude: southEastLngLat[0] },
            };
        };

        const resetMap = () => {
            if (!center) {
                return;
            }
            view.goTo({
                center: [center!.longitude, center!.latitude],
                zoom,
            }).catch(() => {});
        };

        /**
         * This function serves as a fallback for when the map doesn't have an extent (bbox). We've had
         * errors where that happens and that breaks the code. So what we do here is to move the
         * map by only a few meters/feet and then move it back to its original coordinates so that this
         * forces the map to obtain an extent. If that fails 10 times, then we reset the map entirely.
         */
        const failedExtentRecovery = () => {
            if (retriesRef.current < ESRI_EXTENT_RECOVERY_RETRY_LIMIT) {
                retriesRef.current++;
                resetExtent();
            } else {
                retriesRef.current = 0;
                resetMap();
            }
        };

        const applyChanges = (extent?: Extent, zoom?: number) => {
            const currentCenter = {
                latitude: (extent || view).center.latitude,
                longitude: (extent || view).center.longitude,
            };
            const marginBounds = getMarginBounds();

            if (!marginBounds) {
                failedExtentRecovery();

                return;
            }

            retriesRef.current = 0;

            mapOnChange({
                center: currentCenter,
                zoom: zoom ? zoom : view.zoom,
                marginBounds,
            });
            prevCenterRef.current = currentCenter;
        };

        view.watch(
            'zoom',
            debounce((newZoom: number, oldZoom: number) => {
                if (Math.round(newZoom) === Math.round(oldZoom)) {
                    return;
                }

                applyChanges(undefined, newZoom);
                prevZoomRef.current = newZoom;
            }, 50)
        );

        view.watch('ready', (isReady) => {
            if (!isReady || readyRef.current) {
                return;
            }

            applyChanges();
            prevZoomRef.current = view.zoom;
            extentRef.current = view.extent;
            readyRef.current = true;

            if (!view.extent) {
                view.goTo(
                    extentRef.current || {
                        center: [center?.longitude, center?.latitude],
                    }
                ).catch(() => {});
            }
            setIsMapLoading(false);
            // A restored view is already where the user left it.
            if (shouldResetMapOnInitialLoad && !restored) {
                // when map opens for the fist time at default center, reset map to department zoom level. (we have a default zoom level that is more closer for viewing entities on map. Only for the first map load, we set map to department zoom level)
                // resetMap will reset map center and zoom level. rest center is noop here since map is already on default center. calling resetMap just for department zoom level.
                resetMap();
            }
        });

        view.watch(
            'extent',
            debounce((newExtent: Extent | undefined) => {
                if (!newExtent) {
                    // TODO: Determine if we will need something like this in the off chance that the
                    // new extent is null again.
                    if (prevCenterRef.current) {
                        failedExtentRecovery();
                    }

                    return;
                }

                retriesRef.current = 0;

                if (!extentRef.current || !newExtent.equals(extentRef.current)) {
                    applyChanges(newExtent);
                    extentRef.current = newExtent;
                    prevZoomRef.current = view.zoom;
                }
            }, 50)
        );

        view.map.allLayers.on('change', (event) => {
            // change event fires after an item has been added, moved or removed from the collection.
            // event.added returns an array of added layers
            if (event.added.length > 0) {
                // reorder server side layers so they show up beneath client side ones
                const serverSideLayers = event.added
                    // @ts-expect-error Property 'portalItem' does not exist on type 'Layer'.ts(2339)
                    .filter((layer) => !!layer.portalItem)
                    .reverse();

                serverSideLayers.forEach((layer) => webmap.reorder(layer, 0));
            }
        });

        view.on('layerview-create-error', (event) => {
            incrementLayerviewCreateErrorCount();
            logMapError({
                source: 'sdk',
                event: 'layerview_create_error',
                extraData: {
                    layerId: event?.layer?.id,
                    layerTitle: event?.layer?.title,
                },
            });
        });

        view.ui.components = [];
        setWebMap(webmap);

        // No cleanup is returned on purpose. `mapView` is a dependency of this effect
        // and is set from inside it, so a cleanup registered here runs immediately
        // after mount — before the async `view.when()` block above has assigned
        // anything to clean up — and never again on the real unmount. Teardown lives
        // in `useOwnedMapView`, keyed on the view; view-scoped cleanup is contributed
        // with `addDisposer`.
        // The dispatch function won't change.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        center?.latitude,
        center?.longitude,
        mapView,
        mapRef,
        isAlternativeBasemap,
        webmapPortalId,
        shouldLoadPortalWebMapFirst,
        portalWebMapLoadResponse,
        mapOnChange,
        createMapView,
        addDisposer,
        getEsriToken,
    ]);

    return {
        webMap,
        mapView,
        webGLUnavailable,
        isMapLoading,
        isMapLoadingFailed,
        setMapLoadFailed,
    };
};

type UseEsriBaseMapProps = {
    zoom: number;
    mapOnChange: (data: MapOnChangeParams) => void;
    center?: GeoPoint;
    isAlternativeBasemap?: boolean;
    webmapPortalId?: string;
    shouldResetMapOnInitialLoad?: boolean;
    mapMode?: MapMode;
    viewpointToRestore?: MapViewpoint;
    onViewTornDown?: (viewpoint: MapViewpoint) => void;
};
export const useEsriBaseMap = ({
    zoom,
    mapOnChange,
    center,
    isAlternativeBasemap,
    webmapPortalId,
    shouldResetMapOnInitialLoad,
    mapMode,
    viewpointToRestore,
    onViewTornDown,
}: UseEsriBaseMapProps): EsriMapInterface & {
    mapComponent: React.ReactElement;
    mapRef: React.MutableRefObject<HTMLDivElement | null>;
} => {
    const mapRef = useRef<HTMLDivElement | null>(null);
    // The surface attribute is how a leaked WebGL context is traced back to this map:
    // the registry reads the nearest `data-map-surface` ancestor of each canvas.
    const mapComponent = (
        <Box
            w="100%"
            h="100%"
            ref={mapRef}
            data-testid="MAP_COMPONENT"
            {...{ [MAP_SURFACE_ATTRIBUTE]: MAIN_MAP_SURFACE }}
        />
    );
    const {
        mapView,
        webMap,
        webGLUnavailable,
        isMapLoading,
        isMapLoadingFailed,
        setMapLoadFailed,
    } = useEsriESModuleMap({
        zoom,
        mapRef,
        mapOnChange,
        center,
        isAlternativeBasemap,
        webmapPortalId,
        shouldResetMapOnInitialLoad,
        mapMode,
        viewpointToRestore,
        onViewTornDown,
    });

    return {
        mapComponent,
        mapView,
        webMap,
        mapRef,
        webGLUnavailable,
        isMapLoading,
        isMapLoadingFailed,
        setMapLoadFailed,
    };
};
